import { Flex, Stack } from '@chakra-ui/react';
import { useUserContext } from 'context/UserContextProvider';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import Link from '../../components/Link';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { useCountryName } from '../../helpers/localization';
import useChainWorkshopsQuery from '../../hooks/useChainWorkshopsQuery';
import { ContentCard } from '../../layout/ContentCard';

const ChainDetailsWorkshopsCard = (props: { chainId: number }) => {
  const { chainId } = props;
  const { t } = useTranslation();

  const { statusCodes } = useUserContext();
  const workshopCodes = statusCodes.workshop;

  const chainWorkshopsQuery = useChainWorkshopsQuery(chainId);

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return <Link to={`/app/op/workshops/${row.original.id}`}>{row.original.user_readable_id}</Link>;
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        // @ts-ignore
        Cell: ({ value }) => <StatusTag resourceKey={'workshop'} currentStatus={value} />,
      },
      {
        Header: t('workshop:email.label'),
        accessor: 'email',
      },
      {
        Header: t('workshop:name.label'),
        accessor: 'name',
      },
      {
        Header: t('workshop:firstname.label'),
        accessor: 'firstname',
      },
      {
        Header: t('workshop:lastname.label'),
        accessor: 'lastname',
      },
      {
        Header: t('workshop:country.label'),
        accessor: 'country',
        // @ts-ignore
        Cell: ({ value }) => useCountryName(value),
      },
      {
        Header: t('workshop:registered_at.label'),
        accessor: (row: { registered_at: string }) => dateTimeTransformer(row.registered_at) ?? '-',
      },

      {
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Flex alignContent={'flex-end'}>
              <Link ml={'auto'} to={`/app/op/workshops/${row.original.id}`} data-test-id={'edit-' + row.index}>
                {t('common:edit')}
              </Link>
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workshopCodes],
  );
  if (chainWorkshopsQuery.isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <ContentCard
      isLoading={chainWorkshopsQuery.isFetching}
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('pages:chains.details.workshops.title')}
      data-test-id="chainWorkshops-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            isMoreThenOnePage={false}
            data-test-id="chainWorkshops-table"
            data={chainWorkshopsQuery.data as unknown as any[]}
            hasFilters={false}
            columns={columns}
            loading={chainWorkshopsQuery.isFetching}
            hasSearchInput={undefined as any}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default ChainDetailsWorkshopsCard;
